import { Value } from '@philips-internal/oswald-ui-core';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { getFoundationDataContext } from '../app/bootstrap-foundation';
import { APPLICATION_ENDPOINTS } from '../constants/application-endpoints';
import { APPOINTMENT_TYPE } from '../constants/appointment-type';
import { CONTEXT_ATTRIBUTE } from '../constants/context-attribute';
import { ScheduleItem } from '../interfaces/schedules-suggestions';
import { SchedulingDisplayRules } from '../interfaces/scheduling-display-rules';
import { SideBarContext } from '../interfaces/side-bar';
import { CONDITION } from '@philips-emr/patient-components/src/constants/application-constants';
import { ScheduleType } from '../constants/schedule-type';

export class AppointmentUtils {

  /**
   * returns the price or an empty string based on the tasy parameter.
   * @param price item value
   * @param appointmentType 'C'|'S'|'E'
   * @param shouldDisplayTheExamValue 'S'|'N'
   * @param shouldDisplayTheConsultationValue 'S'|'N'
   */
  static getPriceByAppointmentType(price: string, appointmentType: string,
    shouldDisplayTheExamValue: string, shouldDisplayTheConsultationValue: string): string {

    switch (appointmentType) {
      case 'E':
        return shouldDisplayTheExamValue === CONDITION.YES ? price : '';
      case 'C':
        return shouldDisplayTheConsultationValue === CONDITION.YES ? price : '';
      default:
        return '';
    }
  }

  /**
   * Check if the total should be displayed validating the suggestions.
   * @param suggestionItems ScheduleItem[]
   */
  static isShowTotal(suggestionItems: ScheduleItem[]): boolean {
    let isShowTotal = true;

    const examsQtd = suggestionItems.filter(item => item.appointmentType ===  ScheduleType.EXAM)?.length || 0;
    const consultationsQtd = suggestionItems.filter(item => item.appointmentType === ScheduleType.CONSULTATION)?.length || 0;

    const shouldDisplayTheConsultationValue = suggestionItems[0]?.shouldDisplayTheConsultationValue || CONDITION.NO;
    const shouldDisplayTheExamValue = suggestionItems[0]?.shouldDisplayTheExamValue || CONDITION.NO;

    const shouldHideConsultationTotal = consultationsQtd > 0 && shouldDisplayTheConsultationValue !== CONDITION.YES;
    const shouldHideExamTotal = examsQtd > 0 && shouldDisplayTheExamValue !== CONDITION.YES;

    if ((shouldHideConsultationTotal && examsQtd == 0)
      || (shouldHideExamTotal && consultationsQtd == 0)
      || (shouldHideConsultationTotal && shouldHideExamTotal)) {
      isShowTotal = false;
    }

    return isShowTotal;
  }

  /**
   * returns the configuration about witch categories should be shown in appointments filter.
   */
  static getDisplayConfig(): Promise<Record<string, Value>[]> {
    return HttpService.post(APPLICATION_ENDPOINTS.SCHEDULING_CONFIGURATION, {}).then(response => {
      const schedulingDisplayRules = response.body as SchedulingDisplayRules;
      return this.buildDisplayRules(schedulingDisplayRules);
    });
  }

  /**
   * returns the builded display rules for appointments filter based on tasy parameters.
   * @param schedulingDisplayRules tasy parameters that defines witch categories should be shown in filter.
   */
  static buildDisplayRules(schedulingDisplayRules: SchedulingDisplayRules): Record<string, Value>[] {
    const defaultDisplayRules: Record<string, Value>[] = [];
    if (schedulingDisplayRules.checkupVisible === CONDITION.YES) {
      defaultDisplayRules.push({
        label: MessageService.translate('related.items.checkup'),
        value: APPOINTMENT_TYPE.CHECKUP,
      });
    }
    if (schedulingDisplayRules.consultationVisible === CONDITION.YES) {
      defaultDisplayRules.push({
        label: MessageService.translate('related.items.consultations'),
        value: APPOINTMENT_TYPE.CONSULTATIONS,
      });
    }
    if (schedulingDisplayRules.examVisible === CONDITION.YES) {
      defaultDisplayRules.push({
        label: MessageService.translate('related.items.exams'),
        value: APPOINTMENT_TYPE.EXAMS,
      });
    }
    if (schedulingDisplayRules.serviceVisible === CONDITION.YES) {
      defaultDisplayRules.push({
        label: MessageService.translate('related.items.services'),
        value: APPOINTMENT_TYPE.SERVICES,
      });
    }
    if (schedulingDisplayRules.surgeryVisible === CONDITION.YES) {
      defaultDisplayRules.push({
        label: MessageService.translate('related.items.surgeries'),
        value: APPOINTMENT_TYPE.SURGERIES,
      });
    }
    if (schedulingDisplayRules.chemotherapyVisible === CONDITION.YES) {
      defaultDisplayRules.push({
        label: MessageService.translate('related.items.chemotherapy'),
        value: APPOINTMENT_TYPE.CHEMOTHERAPIES,
      });
    }
    defaultDisplayRules.push({
      label: MessageService.translate('patient.request'),
      value: APPOINTMENT_TYPE.REQUEST,
    });

    return defaultDisplayRules;
  }

  /**
   * returns context used in sidebar configuration.
   */
  static getAppointmentContext(): SideBarContext {
    const appContext = getFoundationDataContext();
    const appointmentContext = appContext.get(CONTEXT_ATTRIBUTE.SIDEBAR_CONTEXT);

    return appointmentContext || {};
  }
}
